import React, { useState } from "react";
import jwt from "jwt-decode";
import CssBaseline from "@mui/material/CssBaseline";
import { Alert } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";
import { useNavigate } from "react-router-dom";
import loginService from "../services/login";
import authService from "../services/auth";
import dashboardModuleService from "../services/dashboardModuleService";
import { resolveUserRole } from "../utils/resolveUserRole";
import {
  FALLBACK_CATALOG,
  getDefaultRoute,
  resolveModuleKeys,
} from "../config/rolePermissions";
import { getRegisteredModuleKeys } from "../config/routeRegistry";
import { initToken } from "../utils/constant";

const Login = () => {
  const navigate = useNavigate();
  const [values, setValues] = useState({
    username: "",
    password: "",
    showPassword: false,
    rememberMe: false,
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({
    username: "",
    password: "",
  });
  const [showAlert, setShowAlert] = useState({ message: "", isError: false });

  const handleChange = (field) => (event) => {
    const value =
      field === "rememberMe" ? event.target.checked : event.target.value;
    setValues((prev) => ({ ...prev, [field]: value }));
  };

  const togglePassword = () => {
    setValues((prev) => ({ ...prev, showPassword: !prev.showPassword }));
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setErrors({});
    const err = {};

    if (!values.username.trim()) {
      err.username = "Please enter your username";
    }
    if (!values.password) {
      err.password = "Please enter your password";
    }
    if (Object.keys(err).length > 0) {
      setErrors(err);
      return;
    }

    try {
      setLoading(true);
      const response = await loginService.login({
        username: values.username.trim(),
        password: values.password,
      });

      const decodedToken = jwt(response.data.token);
      const currentTime = Date.now() / 1000;

      if (decodedToken.exp < currentTime) {
        setShowAlert({
          message: "Session has expired. Please login again.",
          isError: true,
        });
        setTimeout(() => setShowAlert({ message: "", isError: false }), 3000);
        return;
      }

      const storage = values.rememberMe
        ? window.localStorage
        : window.sessionStorage;
      let role = resolveUserRole({
        username: values.username.trim(),
        role: response.data.role ?? decodedToken.role,
      });
      let modules;
      let catalog = FALLBACK_CATALOG;

      try {
        const [meRes, catalogRes] = await Promise.all([
          authService.getAuth(response.data.token),
          dashboardModuleService.getCatalog(response.data.token).catch(() => null),
        ]);
        role = resolveUserRole(meRes.data) || role;
        catalog =
          catalogRes?.data?.catalog ||
          (Array.isArray(catalog) && catalog.length ? catalog : FALLBACK_CATALOG);
        modules = resolveModuleKeys(role, meRes.data?.modules, catalog);
      } catch (_) {
        modules = resolveModuleKeys(role, undefined, catalog);
      }

      const registeredModuleKeys = getRegisteredModuleKeys(() => {});
      const data = JSON.stringify({
        decodedToken,
        token: response.data.token,
        role,
        modules,
        catalog,
      });
      storage.setItem("cblife_logged_admin", data);

      if (!values.rememberMe) {
        window.localStorage.removeItem("cblife_logged_admin");
      } else {
        window.sessionStorage.removeItem("cblife_logged_admin");
      }

      initToken(response.data.token, role);
      navigate(getDefaultRoute(modules, catalog, registeredModuleKeys));
    } catch (error) {
      console.log("Login Error", error);

      if (error.response?.data?.error) {
        setErrors({
          username: error.response.data.error.includes("username")
            ? error.response.data.error
            : "",
          password: error.response.data.error.includes("password")
            ? error.response.data.error
            : "",
        });
        setShowAlert({
          message: error.response.data.error,
          isError: true,
        });
        setTimeout(() => setShowAlert({ message: "", isError: false }), 3000);
      } else {
        const isNetworkError = error?.message === "Network Error";
        const networkMessage = isNetworkError
          ? "Cannot reach the login server (network or CORS). Check API URL and server CORS for this admin domain."
          : "An unexpected error occurred. Please try again.";
        setShowAlert({
          message: networkMessage,
          isError: true,
        });
        setTimeout(() => setShowAlert({ message: "", isError: false }), 3000);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <CssBaseline />
      <div className="login-page">
        <div className="login-card">
          <aside
            className="login-card-visual"
            style={{ backgroundImage: "url(/login_image.jpg)" }}
            aria-hidden="true"
          />

          <div className="login-card-form">
            {showAlert.message && (
              <Alert
                severity={showAlert.isError ? "error" : "success"}
                className="login-alert"
              >
                {showAlert.message}
              </Alert>
            )}

            <div className="login-form-brand">
              <span className="login-form-brand-icon">
                <VerifiedUserOutlinedIcon fontSize="small" />
              </span>
              <h1 className="login-form-title">Welcome back!</h1>
              <p className="login-form-subtitle">
                Please login to continue to CBLife Admin Panel
              </p>
            </div>

            <form
              className="login-form"
              onSubmit={handleLogin}
              noValidate
              autoComplete="off"
            >
              <div className="login-field">
                <label className="login-label" htmlFor="username">
                  Username
                </label>
                <div
                  className={`login-input-wrap${
                    errors.username ? " login-input-wrap--error" : ""
                  }`}
                >
                  <PersonOutlineOutlinedIcon className="login-input-icon" />
                  <input
                    id="username"
                    className="login-input"
                    type="text"
                    name="cblife-admin-username"
                    placeholder="Enter your username"
                    value={values.username}
                    onChange={handleChange("username")}
                    autoComplete="off"
                  />
                </div>
                {errors.username && (
                  <p className="login-field-error">{errors.username}</p>
                )}
              </div>

              <div className="login-field">
                <label className="login-label" htmlFor="password">
                  Password
                </label>
                <div
                  className={`login-input-wrap${
                    errors.password ? " login-input-wrap--error" : ""
                  }`}
                >
                  <input
                    id="password"
                    className="login-input login-input--password"
                    type={values.showPassword ? "text" : "password"}
                    name="cblife-admin-password"
                    placeholder="Enter your password"
                    value={values.password}
                    onChange={handleChange("password")}
                    autoComplete="off"
                  />
                  <button
                    type="button"
                    className="login-password-toggle"
                    onClick={togglePassword}
                    aria-label={
                      values.showPassword ? "Hide password" : "Show password"
                    }
                  >
                    {values.showPassword ? (
                      <VisibilityOffOutlinedIcon fontSize="small" />
                    ) : (
                      <VisibilityOutlinedIcon fontSize="small" />
                    )}
                  </button>
                </div>
                {errors.password && (
                  <p className="login-field-error">{errors.password}</p>
                )}
              </div>

              <label className="login-remember">
                <input
                  type="checkbox"
                  checked={values.rememberMe}
                  onChange={handleChange("rememberMe")}
                />
                <span>Remember me</span>
              </label>

              <LoadingButton
                type="submit"
                variant="contained"
                loading={loading}
                className="login-submit"
                fullWidth
              >
                Login
              </LoadingButton>
            </form>

            <p className="login-footer">
              © 2012 CB Life Insurance Company Ltd. All rights reserved.
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default Login;
